import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  Public as PortalIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';
import { tenantService } from '../../services/tenantService';

interface TenantData {
  code: string;
  name: string;
  description?: string;
  portalDefaultWorkflowId?: string;
  portalDefaultStatusId?: string;
  portalWelcomeMessage?: string;
  portalRequestConfirmationMessage?: string;
}

interface Workflow {
  id: string;
  name: string;
  description?: string;
  statuses?: TaskStatus[];
}

interface TaskStatus {
  id: string;
  name: string;
  color: string;
  orderIndex: number;
}

interface Category {
  id: string;
  name: string;
  workflowId: string;
}

export const PortalSettings = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<TenantData>({
    code: tenantService.getCurrentTenantCode(),
    name: '',
    portalDefaultWorkflowId: '',
    portalDefaultStatusId: '',
    portalWelcomeMessage: '',
    portalRequestConfirmationMessage: '',
  });
  
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadedCategoriesForWorkflow, setLoadedCategoriesForWorkflow] = useState<string | null>(null);

  // Load tenant settings
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      if (!mounted) return;
      const workflowData = await loadWorkflows();
      if (!mounted) return;
      await loadSettings(workflowData);
    };
    
    init();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Load stages and categories when workflow changes
  useEffect(() => {
    if (settings.portalDefaultWorkflowId && workflows && workflows.length > 0) {
      const workflow = workflows.find(w => w.id === settings.portalDefaultWorkflowId);
      if (workflow) {
        // Only load if we haven't loaded statuses for this workflow yet
        if (!workflow.statuses) {
          loadTaskStatuses(workflow.id).then((statuses) => {
            // Update selectedWorkflow with loaded statuses
            setSelectedWorkflow({ ...workflow, statuses });
            
            // If no status is selected or the current status doesn't belong to this workflow,
            // default to the first status
            if (statuses && statuses.length > 0) {
              const currentStatusValid = statuses.some(s => s.id === settings.portalDefaultStatusId);
              if (!currentStatusValid) {
                setSettings(prev => ({ 
                  ...prev, 
                  portalDefaultStatusId: statuses[0].id 
                }));
              }
            }
          });
        } else {
          // Statuses already loaded, just update selectedWorkflow
          setSelectedWorkflow(workflow);
        }
        
        // Only load categories if we haven't loaded them for this workflow yet
        if (loadedCategoriesForWorkflow !== workflow.id) {
          loadCategories(workflow.id);
          setLoadedCategoriesForWorkflow(workflow.id);
        }
      } else {
        setSelectedWorkflow(null);
      }
    } else {
      setSelectedWorkflow(null);
    }
  }, [settings.portalDefaultWorkflowId, workflows.length, loadedCategoriesForWorkflow]);

  const loadSettings = async (availableWorkflows: Workflow[] = []) => {
    try {
      const tenantCode = tenantService.getCurrentTenantCode();
      if (!tenantCode) return;

      const response = await dataProvider.getOne('tenants', {
        id: tenantCode,
      });

      if (response.data) {
        const tenantData = response.data;
        
        // Set default empty strings only for null/undefined, not for actual values
        if (tenantData.portalWelcomeMessage === null || tenantData.portalWelcomeMessage === undefined) {
          tenantData.portalWelcomeMessage = '';
        }
        if (tenantData.portalRequestConfirmationMessage === null || tenantData.portalRequestConfirmationMessage === undefined) {
          tenantData.portalRequestConfirmationMessage = '';
        }
        
        // Don't overwrite existing IDs
        if (tenantData.portalDefaultWorkflowId === null || tenantData.portalDefaultWorkflowId === undefined) {
          tenantData.portalDefaultWorkflowId = '';
        }
        if (tenantData.portalDefaultStatusId === null || tenantData.portalDefaultStatusId === undefined) {
          tenantData.portalDefaultStatusId = '';
        }
        
        // Only set defaults if no workflow is saved
        if (!tenantData.portalDefaultWorkflowId && availableWorkflows.length > 0) {
          tenantData.portalDefaultWorkflowId = availableWorkflows[0].id;
          
          // Load statuses for the first workflow and set first status as default
          const statuses = await loadTaskStatuses(availableWorkflows[0].id);
          if (statuses && statuses.length > 0) {
            tenantData.portalDefaultStatusId = statuses[0].id;
          }
          
          // Load categories for the first workflow
          await loadCategories(availableWorkflows[0].id);
          setLoadedCategoriesForWorkflow(availableWorkflows[0].id);
        }
        
        setSettings(tenantData);
      }
    } catch (error) {
      console.error('Failed to load tenant settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflows = async () => {
    try {
      const response = await dataProvider.getList('workflows', {
        filter: { isActive: true },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
      });
      setWorkflows(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load workflows:', error);
      notify('Failed to load workflows', { type: 'error' });
      return [];
    }
  };

  const loadTaskStatuses = async (workflowId: string) => {
    try {
      const response = await dataProvider.getList('task-statuses', {
        filter: { workflowId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'orderIndex', order: 'ASC' },
      });
      
      // Update workflows array
      const updatedWorkflows = workflows.map(w => 
        w.id === workflowId ? { ...w, statuses: response.data } : w
      );
      setWorkflows(updatedWorkflows);
      
      // Update selectedWorkflow if it's the current one
      if (selectedWorkflow?.id === workflowId) {
        setSelectedWorkflow({ ...selectedWorkflow, statuses: response.data });
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to load workflow statuses:', error);
      return [];
    }
  };

  const loadCategories = async (workflowId: string) => {
    try {
      const response = await dataProvider.getList('task-categories', {
        filter: { workflowId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'orderIndex', order: 'ASC' },
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dataProvider.update('tenants', {
        id: settings.code,
        data: {
          portalDefaultWorkflowId: settings.portalDefaultWorkflowId,
          portalDefaultStatusId: settings.portalDefaultStatusId,
          portalWelcomeMessage: settings.portalWelcomeMessage,
          portalRequestConfirmationMessage: settings.portalRequestConfirmationMessage,
        },
        previousData: settings,
      });
      
      // Reload settings to ensure we have the latest data
      const response = await dataProvider.getOne('tenants', {
        id: settings.code,
      });
      
      if (response.data) {
        setSettings(response.data);
      }
      
      notify('Portal settings saved successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to save portal settings', { type: 'error' });
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="Customer Portal Settings"
        subtitle="Configure how service requests from the customer portal are handled"
        icon={<PortalIcon />}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Workflow Configuration */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TaskIcon /> Default Workflow for Portal Requests
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When customers submit service requests through the portal, they'll be created as tasks using these settings.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Default Workflow</InputLabel>
                    <Select
                      value={settings.portalDefaultWorkflowId || ''}
                      onChange={(e) => {
                        setSettings({ 
                          ...settings, 
                          portalDefaultWorkflowId: e.target.value,
                          portalDefaultStatusId: '', // Reset status when workflow changes
                        });
                      }}
                      label="Default Workflow"
                      required
                    >
                      {workflows.map((workflow) => (
                        <MenuItem key={workflow.id} value={workflow.id}>
                          {workflow.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!selectedWorkflow}>
                    <InputLabel>Initial Status</InputLabel>
                    <Select
                      value={settings.portalDefaultStatusId || ''}
                      onChange={(e) => setSettings({ ...settings, portalDefaultStatusId: e.target.value })}
                      label="Initial Status"
                    >
                      <MenuItem value="">
                        <em>Default status</em>
                      </MenuItem>
                      {selectedWorkflow?.statuses?.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: status.color,
                              }}
                            />
                            {status.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {selectedWorkflow && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Portal requests will be created as "{selectedWorkflow.name}" tasks
                  {settings.portalDefaultStatusId && selectedWorkflow.statuses && 
                    ` starting in the "${selectedWorkflow.statuses.find(s => s.id === settings.portalDefaultStatusId)?.name}" status`}
                </Alert>
              )}
            </Box>

            <Divider />

            {/* Portal Messages */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Portal Messages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customize the messages shown to customers in the portal.
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label="Welcome Message"
                  fullWidth
                  multiline
                  rows={2}
                  value={settings.portalWelcomeMessage || ''}
                  onChange={(e) => setSettings({ ...settings, portalWelcomeMessage: e.target.value })}
                  placeholder="Welcome to our customer portal. Submit a service request and we'll get back to you promptly."
                  helperText="Shown on the portal homepage"
                />

                <TextField
                  label="Request Confirmation Message"
                  fullWidth
                  multiline
                  rows={2}
                  value={settings.portalRequestConfirmationMessage || ''}
                  onChange={(e) => setSettings({ ...settings, portalRequestConfirmationMessage: e.target.value })}
                  placeholder="Thank you for your service request. We'll review it and contact you shortly."
                  helperText="Shown after a customer submits a service request"
                />
              </Stack>
            </Box>

            <Divider />

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};